import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, Receipt, Send, TrendingUp, Clock, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { AccountTile, CardTile } from "@/components/Tiles";
import FxPanel from "@/components/FxPanel";

const actions = [
  ["/transfer?tab=internal", "Transfer", ArrowLeftRight], ["/transfer?tab=zelle", "Send with Zelle", Send],
  ["/billpay", "Pay bills", Receipt], ["/invest", "Trade", TrendingUp],
  ["/transfer?tab=external", "Link external account", Link2],
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/dashboard").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (card) => {
    try {
      await api.post(`/cards/${card.id}/lock`, { locked: !card.locked });
      toast.success(card.locked ? "Card unlocked" : "Card locked");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="text-slate-500" data-testid="dashboard-loading">Loading accounts...</div>;
  const total = data.accounts.reduce((s, a) => s + a.balance, 0);
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid="dashboard">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10 fade-up">
        <div>
          <p className="text-sm font-semibold text-[var(--red)] uppercase tracking-[0.2em]">{greet}</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[var(--navy)] mt-2" data-testid="welcome-message">Welcome, {data.user.name}</h1>
          <p className="text-slate-500 mt-2">Total deposit & investment balance <span className="font-semibold text-[var(--navy)]" data-testid="total-balance">{money(total)}</span></p>
        </div>
        {data.pending_count > 0 && (
          <Link to="/transfer?tab=external" className="flex items-center gap-2 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-4 py-2" data-testid="pending-banner">
            <Clock size={15} /> {data.pending_count} transfer(s) pending verification
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-12">
        {actions.map(([to, label, Icon]) => (
          <Link key={label} to={to} className="flex items-center gap-3 bg-white border rounded-xl px-4 py-4 hover:border-[var(--blue)] transition-colors" data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <Icon size={18} className="text-[var(--blue)]" /><span className="font-semibold text-sm text-[var(--navy)]">{label}</span>
          </Link>
        ))}
      </div>
      <h2 className="text-lg font-bold text-[var(--navy)] mb-4">Bank accounts</h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
        {data.accounts.map((a, i) => <AccountTile key={a.id} acc={a} i={i} />)}
      </div>
      <h2 className="text-lg font-bold text-[var(--navy)] mb-4">Credit cards</h2>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {data.cards.map((c, i) => <CardTile key={c.id} card={c} i={i} onToggle={toggle} />)}
        {data.cards.length === 0 && <p className="text-slate-500 text-sm">No credit cards on this profile.</p>}
      </div>
      <FxPanel />
    </div>
  );
}
